import { useState } from 'react';

interface CreateCustomerModalProps {
  onClose: () => void;
  onCustomerCreated: (customer: any) => void;
}

export default function CreateCustomerModal({ onClose, onCustomerCreated }: CreateCustomerModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    loyaltyProgram: true,
    marketingConsent: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      const newCustomer = {
        id: `CUST-${Date.now()}`,
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        email: formData.email,
        loyaltyId: formData.loyaltyProgram ? `LP-${Math.floor(Math.random() * 100000)}` : null,
        points: 0,
        tier: 'Bronze',
        joinDate: new Date().toISOString().split('T')[0],
        totalSpent: 0,
        visits: 0,
        address: formData.address ? {
          street: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        } : null,
      };

      setIsSubmitting(false);
      onCustomerCreated(newCustomer);
      onClose();
    }, 1000);
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-teal-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Create New Customer</h2>
            <p className="text-sm text-teal-100 whitespace-nowrap">Add customer details and enroll in loyalty program</p>
          </div>
          <button 
            onClick={onClose}
            disabled={isSubmitting}
            className="w-8 h-8 flex items-center justify-center hover:bg-teal-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-user-line text-teal-600"></i>
                Personal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300 focus:border-teal-500'
                    }`}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300 focus:border-teal-500'
                    }`}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', formatPhoneNumber(e.target.value))}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      errors.phone ? 'border-red-500' : 'border-gray-300 focus:border-teal-500'
                    }`}
                    placeholder="(555) 123-4567"
                    maxLength={14}
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      errors.email ? 'border-red-500' : 'border-gray-300 focus:border-teal-500'
                    }`}
                    placeholder="customer@example.com"
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-map-pin-line text-teal-600"></i>
                Address Information (Optional)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="NY"
                    maxLength={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="10001"
                    maxLength={5}
                  />
                </div>
              </div>
            </div>

            {/* Preferences */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-settings-3-line text-teal-600"></i>
                Preferences
              </h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 bg-teal-50 border-2 border-teal-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.loyaltyProgram}
                    onChange={(e) => handleChange('loyaltyProgram', e.target.checked)}
                    disabled={isSubmitting}
                    className="w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">Enroll in Loyalty Program</p>
                    <p className="text-xs text-gray-600 whitespace-nowrap">Earn points on every purchase and get exclusive rewards</p>
                  </div>
                  <i className="ri-gift-line text-2xl text-teal-600"></i>
                </label>

                <label className="flex items-center gap-3 p-3 bg-gray-50 border-2 border-gray-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.marketingConsent}
                    onChange={(e) => handleChange('marketingConsent', e.target.checked)}
                    disabled={isSubmitting}
                    className="w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">Receive Marketing Communications</p>
                    <p className="text-xs text-gray-600 whitespace-nowrap">Get special offers, promotions, and updates via email/SMS</p>
                  </div>
                  <i className="ri-mail-line text-2xl text-gray-400"></i>
                </label>
              </div>
            </div>

            {formData.loyaltyProgram && (
              <div className="p-4 bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-200 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-full">
                    <i className="ri-vip-crown-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 whitespace-nowrap">Loyalty Program Benefits</p>
                    <p className="text-xs text-gray-600 whitespace-nowrap">Customer will start at Bronze tier</p>
                  </div>
                </div>
                <ul className="space-y-1 ml-13">
                  <li className="text-xs text-gray-700 flex items-center gap-2">
                    <i className="ri-check-line text-teal-600"></i>
                    Earn 1 point for every $1 spent
                  </li>
                  <li className="text-xs text-gray-700 flex items-center gap-2">
                    <i className="ri-check-line text-teal-600"></i>
                    Birthday rewards and special offers
                  </li>
                  <li className="text-xs text-gray-700 flex items-center gap-2">
                    <i className="ri-check-line text-teal-600"></i>
                    Exclusive member-only promotions
                  </li>
                </ul>
              </div>
            )}
          </div>
        </form>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
              isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer'
            }`}
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Creating...
              </>
            ) : (
              <>
                <i className="ri-user-add-line mr-2"></i>
                Create Customer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
